import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { NotificationService } from '../services/notification.service';

/**
 * Permite a una petición puntual desactivar el toast automático de error
 * (ej. cuando el componente quiere manejar el error a su manera):
 *   this.api.get('x', { context: skipErrorNotify() })
 */
export const SKIP_ERROR_NOTIFY = new HttpContextToken<boolean>(() => false);

/**
 * Interceptor global de errores HTTP: muestra un toast con un mensaje claro.
 * El 401 lo gestiona el `authInterceptor` (refresh/logout); aquí solo se
 * notifica. Es el "notificador cuando algo no se envía correctamente".
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const notify = inject(NotificationService);

  return next(req).pipe(
    catchError((err: HttpErrorResponse) => {
      if (!req.context.get(SKIP_ERROR_NOTIFY)) {
        notify.error(mapErrorMessage(err));
      }
      return throwError(() => err);
    }),
  );
};

/**
 * Traduce un error HTTP a un mensaje entendible para el usuario.
 *
 * El backend envía el envelope con un `message` ya localizado (Accept-Language),
 * así que se prioriza ese texto. Los fallbacks por status cubren los casos sin
 * cuerpo legible (ej. error de red, gateway caído).
 */
function mapErrorMessage(err: HttpErrorResponse): string {
  const serverMsg = (err.error?.message ?? err.error?.error) as string | undefined;
  if (serverMsg) return serverMsg;

  switch (err.status) {
    case 0:   return 'No se pudo conectar con el servidor. Revisa tu conexión.';
    case 400: return 'Solicitud inválida.';
    case 401: return 'Tu sesión expiró. Inicia sesión nuevamente.';
    case 403: return 'No tienes permisos para realizar esta acción.';
    case 404: return 'No se encontró el recurso solicitado.';
    case 409: return 'Conflicto con el estado actual del recurso.';
    case 422: return 'Hay datos inválidos. Revisa el formulario.';
    case 500: return 'Error interno del servidor. Inténtalo más tarde.';
    default:  return `Ocurrió un error inesperado (${err.status}).`;
  }
}
